import type { AudioCaptureConstraints, MediaDeviceInfoLike, MediaPlatform, MediaStreamLike, PermissionStateLike, PermissionWatch } from './platform.js';
import { fail, ok, type Disposer, type EngineErrorCode, type Result } from './result.js';

export type MicrophonePermission = 'unknown' | 'prompt' | 'granted' | 'denied';

export interface AudioDevice {
  readonly id: string;
  readonly group_id: string;
  /** Empty until microphone permission is granted (browser privacy rule). */
  readonly label: string;
}

export interface AudioProcessing {
  readonly echoCancellation: boolean;
  readonly noiseSuppression: boolean;
  readonly autoGainControl: boolean;
}

export const DEFAULT_AUDIO_PROCESSING: AudioProcessing = Object.freeze({ echoCancellation: true, noiseSuppression: true, autoGainControl: true });

export interface MediaSnapshot {
  readonly permission: MicrophonePermission;
  readonly inputs: readonly AudioDevice[];
  readonly outputs: readonly AudioDevice[];
  /** null means the system default. */
  readonly selected_input: string | null;
  readonly selected_output: string | null;
  readonly output_selection: 'supported' | 'unsupported';
  readonly playback: 'idle' | 'playing' | 'blocked';
  readonly secure_context: boolean;
  readonly webrtc_supported: boolean;
}

export type MediaEvent =
  | { readonly type: 'device_added'; readonly kind: 'input' | 'output'; readonly device: AudioDevice }
  | { readonly type: 'device_removed'; readonly kind: 'input' | 'output'; readonly device: AudioDevice }
  | { readonly type: 'input_fallback'; readonly from: string | null; readonly reason: 'device_unavailable' | 'device_removed' | 'track_ended' }
  | { readonly type: 'output_fallback'; readonly from: string; readonly reason: 'device_removed' | 'device_unavailable' }
  | { readonly type: 'permission_changed'; readonly permission: MicrophonePermission };

export interface MediaHooks {
  changed(): void;
  emit(event: MediaEvent): void;
  selectedInputRemoved(): void;
  selectedOutputRemoved(): void;
  permissionChanged(permission: MicrophonePermission): void;
}

export interface AcquiredMicrophone {
  readonly stream: MediaStreamLike;
  /** True when the selected device was unavailable and the system default was used. */
  readonly fellBack: boolean;
}

const errorName = (error: unknown): string => (typeof error === 'object' && error !== null && typeof (error as { name?: unknown }).name === 'string' ? (error as { name: string }).name : '');

export function classifyMediaError(error: unknown, secureContext: boolean): { code: EngineErrorCode; message: string; retryable: boolean } {
  switch (errorName(error)) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return { code: 'microphone_denied', message: 'Microphone access is blocked for this app.', retryable: true };
    case 'SecurityError':
      return secureContext
        ? { code: 'microphone_denied', message: 'Microphone access is blocked by browser or OS policy.', retryable: true }
        : { code: 'insecure_context', message: 'Calling requires a secure (https) page.', retryable: false };
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return { code: 'microphone_not_found', message: 'No microphone found. Connect a headset.', retryable: true };
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return { code: 'microphone_in_use', message: 'Another app may be using your microphone.', retryable: true };
    default:
      return { code: 'microphone_unavailable', message: 'The microphone could not be opened.', retryable: true };
  }
}

export function stopStream(stream: MediaStreamLike | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) { try { track.stop(); } catch { /* already stopped */ } }
}

const toDevice = (d: MediaDeviceInfoLike): AudioDevice => Object.freeze({ id: d.deviceId, group_id: d.groupId, label: d.label });

export class MediaController {
  private permission: MicrophonePermission = 'unknown';
  private watch: PermissionWatch | null = null;
  private inputs: readonly AudioDevice[] = [];
  private outputs: readonly AudioDevice[] = [];
  private enumerated = false;
  private selectedInput: string | null = null;
  private selectedOutput: string | null = null;
  private playback: MediaSnapshot['playback'] = 'idle';
  private readonly disposers: Disposer[] = [];
  private enumerating: Promise<void> | null = null;
  private disposed = false;
  private retained: MediaStreamLike | null = null;
  private retaining: Promise<Result<MicrophonePermission>> | null = null;
  private opening: Promise<Result<AcquiredMicrophone>> | null = null;
  private retainGeneration = 0;

  constructor(private readonly platform: MediaPlatform, private readonly processing: AudioProcessing, private readonly hooks: MediaHooks, private readonly retainForSession = false) {}

  async init(): Promise<void> {
    try { this.watch = await this.platform.watchMicrophonePermission(); } catch { this.watch = null; }
    if (this.disposed) return;
    if (this.watch) {
      this.setPermission(mapPermission(this.watch.state));
      this.disposers.push(this.watch.onChange(state => this.setPermission(mapPermission(state))));
    }
    this.disposers.push(this.platform.onDeviceChange(() => { void this.refreshDevices(); }));
    await this.refreshDevices();
  }

  snapshot(): MediaSnapshot {
    return Object.freeze({
      permission: this.permission,
      inputs: this.inputs,
      outputs: this.outputs,
      selected_input: this.selectedInput,
      selected_output: this.selectedOutput,
      output_selection: this.platform.outputSelectionSupported ? 'supported' : 'unsupported',
      playback: this.playback,
      secure_context: this.platform.secureContext,
      webrtc_supported: this.platform.webrtcSupported,
    });
  }

  get currentPermission(): MicrophonePermission { return this.permission; }
  get input(): string | null { return this.selectedInput; }
  get output(): string | null { return this.selectedOutput; }
  get outputSelectionSupported(): boolean { return this.platform.outputSelectionSupported; }

  setPlayback(state: MediaSnapshot['playback']): void {
    if (this.playback !== state) { this.playback = state; this.hooks.changed(); }
  }

  constraints(deviceId: string | null): AudioCaptureConstraints {
    return {
      audio: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        echoCancellation: this.processing.echoCancellation,
        noiseSuppression: this.processing.noiseSuppression,
        autoGainControl: this.processing.autoGainControl,
        channelCount: { ideal: 1 },
      },
      video: false,
    };
  }

  /** Opens exactly one live audio track. Falls back to the system default if the chosen device is gone. */
  async acquire(preferred: string | null = this.selectedInput): Promise<Result<AcquiredMicrophone>> {
    if (this.disposed) return fail('engine_disposed', 'The call engine was disposed.');
    if (!this.platform.secureContext) return fail('insecure_context', 'Calling requires a secure (https) page.');
    if (!this.retainForSession || !this.platform.cloneStream) return this.openMicrophone(preferred);
    let fellBack = false;
    const source = this.retained?.getAudioTracks()[0];
    if (source?.readyState !== 'live' || (preferred && source.getSettings().deviceId !== preferred)) {
      const generation = this.retainGeneration;
      this.opening ??= this.openMicrophone(preferred).then(result => {
        if (result.ok) {
          if (generation !== this.retainGeneration || this.disposed) {
            stopStream(result.value.stream);
            return fail<AcquiredMicrophone>('microphone_unavailable', 'The microphone request was cancelled. Try again when the phone is on.', true);
          }
          this.releaseRetained();
          this.retained = result.value.stream;
          for (const held of this.retained.getAudioTracks()) held.enabled = false;
        }
        return result;
      }).finally(() => { this.opening = null; });
      const opened = await this.opening;
      if (!opened.ok) return opened;
      fellBack = opened.value.fellBack;
    }
    if (this.disposed || !this.retained || this.retained.getAudioTracks()[0]?.readyState !== 'live') return fail('microphone_unavailable', 'The microphone is no longer available. Try Allow microphone again.', true);
    const stream = this.platform.cloneStream(this.retained);
    for (const track of stream.getAudioTracks()) track.enabled = true;
    return ok({ stream, fellBack });
  }

  private async openMicrophone(preferred: string | null): Promise<Result<AcquiredMicrophone>> {
    let stream: MediaStreamLike;
    let fellBack = false;
    try {
      stream = await this.platform.getUserMedia(this.constraints(preferred));
    } catch (error) {
      const name = errorName(error);
      if (preferred && (name === 'OverconstrainedError' || name === 'NotFoundError')) {
        try {
          stream = await this.platform.getUserMedia(this.constraints(null));
          fellBack = true;
          this.hooks.emit({ type: 'input_fallback', from: preferred, reason: 'device_unavailable' });
          if (this.selectedInput === preferred) { this.selectedInput = null; this.hooks.changed(); }
        } catch (second) {
          return this.failure(second);
        }
      } else {
        return this.failure(error);
      }
    }
    if (this.disposed) { stopStream(stream); return fail('engine_disposed', 'The call engine was disposed.'); }
    const audio = stream.getAudioTracks();
    for (const track of stream.getTracks()) if (track !== audio[0]) track.stop();
    const track = audio[0];
    if (!track || track.readyState !== 'live') {
      stopStream(stream);
      return fail('microphone_unavailable', 'The microphone produced no live audio track.', true);
    }
    if (!this.watch) this.setPermission('granted');
    if (this.permission !== 'granted' && this.watch) this.setPermission('granted');
    if (!this.enumerated || this.inputs.some(d => d.label === '')) void this.refreshDevices();
    return ok({ stream, fellBack });
  }

  /** Coalesce gestures. On iPhone the muted source stays alive; independent call tracks can end freely. */
  requestPermission(): Promise<Result<MicrophonePermission>> {
    if (this.retaining) return this.retaining;
    this.retaining = (async () => {
      const acquired = await this.acquire(this.selectedInput);
      if (!acquired.ok) return { ok: false as const, error: acquired.error };
      stopStream(acquired.value.stream);
      await this.refreshDevices();
      return ok(this.permission);
    })().finally(() => { this.retaining = null; });
    return this.retaining;
  }

  releaseRetained(): void { this.retainGeneration++; stopStream(this.retained); this.retained = null; }

  selectInput(deviceId: string | null): Result<void> {
    if (deviceId !== null && this.enumerated && !this.inputs.some(d => d.id === deviceId)) return fail('input_device_unavailable', 'That microphone is not connected.');
    if (deviceId !== this.selectedInput) this.releaseRetained();
    this.selectedInput = deviceId;
    this.hooks.changed();
    return ok(undefined);
  }

  selectOutput(deviceId: string | null): Result<void> {
    if (!this.platform.outputSelectionSupported) return fail('output_selection_unsupported', 'Audio plays through your system output on this browser.');
    if (deviceId !== null && this.enumerated && !this.outputs.some(d => d.id === deviceId)) return fail('output_device_unavailable', 'That speaker is not connected.');
    this.selectedOutput = deviceId;
    this.hooks.changed();
    return ok(undefined);
  }

  /** Re-enumerates; emits added/removed devices but never switches a live call to a new device. */
  refreshDevices(): Promise<void> {
    if (this.enumerating) return this.enumerating;
    this.enumerating = (async () => {
      let list: MediaDeviceInfoLike[];
      try { list = await this.platform.enumerateDevices(); } catch { list = []; }
      if (this.disposed) return;
      const inputs = list.filter(d => d.kind === 'audioinput').map(toDevice);
      const outputs = list.filter(d => d.kind === 'audiooutput').map(toDevice);
      const first = !this.enumerated;
      if (!first) {
        this.diff('input', this.inputs, inputs);
        this.diff('output', this.outputs, outputs);
      }
      this.inputs = Object.freeze(inputs);
      this.outputs = Object.freeze(outputs);
      this.enumerated = true;
      if (this.selectedInput && !inputs.some(d => d.id === this.selectedInput)) {
        const from = this.selectedInput;
        this.selectedInput = null;
        this.hooks.emit({ type: 'input_fallback', from, reason: 'device_removed' });
        this.hooks.selectedInputRemoved();
      }
      if (this.selectedOutput && !outputs.some(d => d.id === this.selectedOutput)) {
        const from = this.selectedOutput;
        this.selectedOutput = null;
        this.hooks.emit({ type: 'output_fallback', from, reason: 'device_removed' });
        this.hooks.selectedOutputRemoved();
      }
      this.hooks.changed();
    })().finally(() => { this.enumerating = null; });
    return this.enumerating;
  }

  dispose(): void {
    this.disposed = true;
    this.releaseRetained();
    for (const dispose of this.disposers.splice(0)) { try { dispose(); } catch { /* ignore */ } }
  }

  private diff(kind: 'input' | 'output', before: readonly AudioDevice[], after: readonly AudioDevice[]): void {
    for (const device of after) if (!before.some(d => d.id === device.id)) this.hooks.emit({ type: 'device_added', kind, device });
    for (const device of before) if (!after.some(d => d.id === device.id)) this.hooks.emit({ type: 'device_removed', kind, device });
  }

  private failure(error: unknown): Result<AcquiredMicrophone> {
    const classified = classifyMediaError(error, this.platform.secureContext);
    if (classified.code === 'microphone_denied') this.setPermission('denied');
    return fail(classified.code, classified.message, classified.retryable);
  }

  private setPermission(permission: MicrophonePermission): void {
    if (this.permission === permission) return;
    this.permission = permission;
    if (permission !== 'granted') this.releaseRetained();
    this.hooks.emit({ type: 'permission_changed', permission });
    this.hooks.permissionChanged(permission);
    this.hooks.changed();
  }
}

function mapPermission(state: PermissionStateLike): MicrophonePermission {
  return state === 'granted' ? 'granted' : state === 'denied' ? 'denied' : 'prompt';
}
